import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";
import { INVESTIGATION_CATALOGUE_ITEM_SCHEMA_LITERAL } from "./investigation-catalogue-item";

export const RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME =
  "investigation-activity";

// Type-safe wrappers for all rxdb-hooks hooks. These should be copy-pasted for each
// collection, lest a type-wizard arrives.
// https://github.com/cvara/rxdb-hooks?tab=readme-ov-file#userxdocument

export const useRxCollectionInvestigationActivity =
  (): RxCollection<InvestigationActivity> | null =>
    useRxCollection<InvestigationActivity>(
      RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
    );
type UseRxData = Parameters<typeof useRxData<InvestigationActivity>>;
export type InvestigationActivityQueryConstructor = UseRxData["1"];
/**
 * @deprecated Use `useRxData<InvestigationActivity>(RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
 * queryConstructor, options)` instead because this abstraction loses the type
 * inference given to you by the return type of the queryConstructor
 */
export const useRxDataInvestigationActivity = (
  queryConstructor: UseRxData["1"],
  options?: UseRxData["2"],
) =>
  useRxData<InvestigationActivity>(
    RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
    queryConstructor,
    options,
  );

// subschemas used more than once defined here for reuse

const INVESTIGATION_REQUEST_OUTCOME = {
  title: "investigation request outcome",
  type: ["object", "null"],
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    clinic: {
      type: "object",
      properties: {
        id: {
          type: "string",
          maxLength: 100,
        },
        name: {
          type: "string",
        },
      },
    },
    meta: {
      type: ["object", "null"],
      properties: {
        updated: {
          type: "string",
          format: "date-time",
        },
        created: {
          type: "string",
          format: "date-time",
        },
      },
    },
    orderedProperties: {
      type: "array",
      items: {
        type: ["object", "null"],
      },
    },
    organisation: {
      type: "object",
      properties: {
        name: {
          type: "string",
        },
        id: {
          type: "string",
          maxLength: 100,
        },
      },
    },
    performer: {
      type: "object",
      properties: {
        id: {
          type: "string",
          maxLength: 100,
        },
        name: {
          type: "string",
        },
      },
    },
  },
  required: [
    "clinic",
    "orderedProperties",
    "organisation",
    "performer",
    "id",
    "meta",
  ],
} as const;

const INVESTIGATION_ACTIVITY_SCHEMA_LITERAL = {
  title: "investigation activity schema",
  version: 9,
  description:
    "Represents the record of a patient's specific investigation result ",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    catalogueItem: INVESTIGATION_CATALOGUE_ITEM_SCHEMA_LITERAL,
    clinicalEvent: {
      type: ["object", "null"],
      properties: {
        id: {
          type: "string",
        },
        type: {
          type: "string",
          enum: ["ANC_ENCOUNTER", "VISIT", "CONSULTATION"],
        },
      },
    },
    meta: SYNCABLE_META,
    outcome: INVESTIGATION_REQUEST_OUTCOME,
    patientId: { type: "string", maxLength: 100 },
    request: INVESTIGATION_REQUEST_OUTCOME,
  },
  required: [
    "id",
    "catalogueItem",
    "meta",
    "patientId",
    "clinicalEvent",
    "outcome",
    "request",
  ],
  // https://elephant.slab.com/posts/offline-schema-fields-uwb0pbii#h48y1-rxdb-dexie-elephant-5-investigation-activity
  encrypted: ["catalogueItem", "meta", "clinicalEvent", "outcome", "request"],

  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;
const schemaTyped = toTypedRxJsonSchema(INVESTIGATION_ACTIVITY_SCHEMA_LITERAL);

// aggregate the document type from the schema
export type InvestigationActivity = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

// create the typed RxJsonSchema from the literal typed object.
export const INVESTIGATION_ACTIVITY_SCHEMA: RxJsonSchema<InvestigationActivity> =
  INVESTIGATION_ACTIVITY_SCHEMA_LITERAL;
